// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## midiInput.js
//
// Handles inputs from midi devices, and translates them into
// events with human-readable properties.

import { uniqueByFilter } from 'shared/src/utils/uniqueByFilter';
import { writable } from 'svelte/store';

export interface ProcessedMidiMessage {
  type: string;
  channel: number;
  midiNumber: number | string;
  /** 0 to 1 */
  velocity: number;
  /** -1 to 1... I think... */
  wheelValue: number;
  timeStamp?: number;
}

const midiCommandDictionary = {
  8: 'noteOff',
  9: 'noteOn',
  10: 'afterTouch', // individual notes
  11: 'controller',
  12: 'programChange',
  13: 'channelAfterTouch', // entire channel
  14: 'wheel',
} as const;

export type MidiMessageType = typeof midiCommandDictionary[keyof typeof midiCommandDictionary];

interface SoundingMidi {
  midi: number;
  velocity: number;
}

export default class MidiInput {
  midiSupported = false;

  midiAccess: WebMidi.MIDIAccess;

  commandNames = midiCommandDictionary;

  eventLog: { time: number; message: ProcessedMidiMessage }[] = [];

  listeners: { [MessageType in MidiMessageType]: Function[] } = {
    noteOff: [],
    noteOn: [],
    afterTouch: [],
    controller: [],
    programChange: [],
    channelAfterTouch: [],
    wheel: [],
  };

  // Public constructor
  constructor(delayInit = false) {
    if (!delayInit) this.init();
    return this;
  }

  private midisStore = writable<{
    sounding: SoundingMidi[];
    added: SoundingMidi[];
    removed: SoundingMidi[];
  }>({
    sounding: [],
    added: [],
    removed: [],
  });

  subscribe = this.midisStore.subscribe;

  init() {
    // once MIDI has started.
    this.initializeMidi();
    return this;
  }

  initializeMidi() {
    if (window.navigator && window.navigator.requestMIDIAccess) {
      window.navigator.requestMIDIAccess().then(this.onMIDIStarted.bind(this), (err) => {
        console.log('Unable to start Midi:');
        console.log(err);
      });
    }
  }

  onMIDIStarted(midiAccess: WebMidi.MIDIAccess) {
    this.midiAccess = midiAccess;

    const midiIns = this.getMidiIns();
    if (midiIns) {
      midiIns.forEach((input) => {
        input.onmidimessage = this.messageHandler.bind(this);
      });
    }
  }

  getMidiIns() {
    const midiAccess = this.midiAccess;
    // Get all available inputs:
    let midiIns: Map<string, WebMidi.MIDIInput> = new Map();
    if (typeof midiAccess.inputs === 'function') {
      // Old School deprecated MIDI inputs() code
      midiIns = (midiAccess as any).inputs();
    } else {
      midiIns = midiAccess.inputs;
    }
    return midiIns;
  }

  messageHandler(ev: WebMidi.MIDIMessageEvent) {
    // console.log(ev);
    let cmd = ev.data[0] >> 4;
    const channel = ev.data[0] & 0xf;
    const midiNumber = ev.data[1];
    const velocity = ev.data[2];

    // with MIDI, note on with velocity zero is the same as note off
    if (cmd === 9 && velocity === 0) cmd = 8;

    const command = this.commandNames[cmd as keyof typeof midiCommandDictionary];

    if (!command) return;

    const midiMessage = {
      type: command,
      channel,
      midiNumber,
      velocity: +(velocity / 127.0).toFixed(3),
      wheelValue: (velocity * 128.0 + midiNumber - 8192) / 8192.0,
    };

    // Channel 9 is percussion... let's ignore it.
    if (channel === 9) return;

    // If it's an undefined type, let's return:
    if (typeof midiMessage.type === 'undefined') return;

    if (this.duplicateLogEvent(midiMessage))
      // Watch for duplicates since we're listening on multiple
      // inputs, and discard any within a small number of MS.
      return;

    // Add a midi if needed:
    if (midiMessage.type === 'noteOn' && midiMessage.midiNumber) {
      this.addMidi(midiMessage.midiNumber, midiMessage.velocity);
    }

    if (midiMessage.type === 'noteOff' && midiMessage.midiNumber) {
      this.removeMidi(midiMessage.midiNumber);
    }

    this.dispatch(midiMessage);
  }

  addMidi(midi: number, velocity: number) {
    if (Math.round(midi) !== midi) {
      throw Error('Midi number must be an integer.');
    }
    // console.log(`Add midi: ${midi}`);
    this.midisStore.update((store) => {
      // If it's already there, no change
      if (store.sounding.find((s) => s.midi === midi)) {
        return {
          sounding: store.sounding,
          added: [],
          removed: [],
        };
      }

      const addedRecord = {
        midi,
        velocity,
      };

      return {
        sounding: [...store.sounding, addedRecord],
        added: [addedRecord],
        removed: [],
      };
    });
  }

  removeMidi(midi: number) {
    // console.log(`Remove midi: ${midi}`);

    this.midisStore.update((store) => {
      const removed = store.sounding.find((s) => s.midi === midi);
      if (!removed) {
        return {
          sounding: store.sounding,
          added: [],
          removed: [],
        };
      }

      return {
        sounding: store.sounding.filter((s) => s.midi !== midi),
        added: [],
        removed: [removed],
      };
    });
  }

  panic() {
    this.midisStore.update((store) => ({
      sounding: [],
      added: [],
      removed: store.sounding,
    }));
  }

  duplicateLogEvent(midiMessage: ProcessedMidiMessage) {
    const time = new Date().getTime();
    // console.log('Event log before filter: ', JSON.parse(JSON.stringify(this.eventLog)));
    this.eventLog = this.eventLog.filter((e) => e.time + 10 > time);
    // console.log(
    //   `Event log AFTER filter (current time ${time}): `,
    //   JSON.parse(JSON.stringify(this.eventLog))
    // );
    const isDuplicate = this.eventLog.find(
      (e) => e.message.type === midiMessage.type && e.message.midiNumber === midiMessage.midiNumber
    );

    if (isDuplicate) {
      // console.log(`duplicate! ${midiMessage.type}: ${midiMessage.midiNumber}`, midiMessage);
      return true;
    }

    // console.log(`New! ${midiMessage.type}: ${midiMessage.midiNumber}`, midiMessage);

    this.eventLog.push({
      time,
      message: midiMessage,
    });
    return false;
  }

  dispatch(midiMessage: ProcessedMidiMessage) {
    //      console.log(midiMessage);
    if (this.listeners.hasOwnProperty(midiMessage.type)) {
      this.listeners[midiMessage.type].forEach((listener) => {
        listener(midiMessage);
      });
    }
  }

  addListener(type: string, callback: Function, thisArg?) {
    if (!this.listeners.hasOwnProperty(type)) {
      this.listeners[type] = [];
    }
    const cb = thisArg ? callback.bind(thisArg) : callback;

    // adds function to be called back when status changes.
    this.listeners[type].push(cb);
    return this;
  }

  stopListeners() {
    if (this.midiSupported) {
      const midiIns = this.getMidiIns();
      midiIns.forEach((input) => {
        input.onmidimessage = null;
      });
      this.listeners = {};
    }
    return this;
  }
}
